import * as React from "react";
import { cn } from "../../lib/utils";
import { Label } from "./label";

interface FieldProps {
  /** Must match the control's `id`. */
  htmlFor: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  optional?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/**
 * Label + control + hint + error, wired for assistive tech. Pass the control
 * as children with the same `id`; the field injects `aria-describedby` and
 * `aria-invalid` for you.
 */
function Field({ htmlFor, label, hint, error, optional, className, children }: FieldProps) {
  const hintId = hint ? `${htmlFor}-hint` : undefined;
  const errorId = error ? `${htmlFor}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })
    : children;

  return (
    <div data-slot="field" className={cn("grid gap-2", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={htmlFor}>{label}</Label>
        {optional && <span className="text-xs text-text-tertiary">{optional}</span>}
      </div>
      {control}
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="text-[13px] leading-snug text-destructive animate-fade-in"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[13px] leading-snug text-text-tertiary">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export { Field };
